/* eslint-disable @typescript-eslint/no-explicit-any, react/no-unused-state */
import React, { Component } from 'react'
import type Bluebird from 'bluebird'
import withApi, { type WithApiInjectedProps } from 'legacy/common/withApi'
import { errors } from 'utils/error-handling/errors'
import { type ApiResponse, type ApiDataState } from 'legacy/common/types'
import { type ApiRequestOpts } from 'legacy/common/instacart'

function getDisplayName(WrappedComponent: React.ComponentType<any>) {
  return WrappedComponent.displayName || WrappedComponent.name || 'Component'
}

type ApiData<TData, TMeta> = ApiDataState<{ data: TData; meta: TMeta }>

type Props = WithApiInjectedProps

// prettier-ignore
export interface WithApiDataInjectedProps<TData, TMeta = {}, TQuery = {}> extends WithApiInjectedProps {
  apiData: ApiData<TData, TMeta>;
  fetchData(path: string, requestOpts: ApiRequestOpts<{}, TQuery>): Promise<void>;
}

// prettier-ignore
type MergedProps<P extends WithApiDataInjectedProps<any, any, any>> =
  Omit<P, keyof WithApiDataInjectedProps<any, any, any>> & Props;

function withApiData<P extends WithApiDataInjectedProps<any, any, any>>(
  WrappedComponent: React.ComponentType<P>
): React.ComponentClass<Omit<MergedProps<P>, keyof WithApiInjectedProps>> {
  class WithApiData extends Component<MergedProps<P>, ApiData<any, any>> {
    static displayName = `WithApiData(${getDisplayName(WrappedComponent)})`

    state: ApiData<any, any> = {
      loading: false,
      loaded: false,
      error: false,
      data: undefined,
      meta: undefined,
    }

    promise: Bluebird<ApiResponse<any, any>> | undefined

    constructor(props: MergedProps<P>) {
      super(props)
      this.fetchData = this.fetchData.bind(this)
    }

    cancelPromise() {
      if (this.promise && this.promise.isPending()) {
        this.promise.cancel()
      }
    }

    async fetchData(path: string, requestOpts: ApiRequestOpts<undefined, any>): Promise<void> {
      this.cancelPromise()
      this.setState({ loading: true, loaded: false, error: false })

      try {
        this.promise = this.props.api.get<ApiResponse<any, any>>(path, requestOpts)
        const result = await this.promise
        const { data, meta } = result

        this.setState({
          data,
          meta,
          loaded: true,
          loading: false,
        })
      } catch (e) {
        errors.captureException(e)

        this.setState({
          data: undefined,
          meta: undefined,
          loaded: false,
          loading: false,
          error: true,
        })
      }
    }

    render() {
      return (
        // cast temporarily
        // Workaround for https://github.com/microsoft/TypeScript/issues/28884
        <WrappedComponent {...(this.props as P)} apiData={this.state} fetchData={this.fetchData} />
      )
    }
  }

  return withApi(WithApiData)
}

export default withApiData
